import React from 'react';
import SectionHeader from './SectionHeader';
import TextInput from './TextInput';
import SelectInput from './SelectInput';
import ChoiceRadio from './ChoiceRadio';
import YesNoRadio from './YesNoRadio';
import AddressFields from './AddressFields';
import NotesInput from './NotesInput';
import { SECURITY_QUESTIONS } from './options';
import { SECTION_DEFAULTS, useSectionDefaults } from './defaults';
import { MIN_DOB, formatPhone, todayIso } from './format';

const COUNTRY_OPTIONS = [
  { value: 'usa', label: 'USA' },
  { value: 'other', label: 'Other (please specify)' },
];

const COUNTRY_FIELDS = [
  { field: 'citizenship', other: 'citizenship_other', label: 'Country(ies) of citizenship' },
  { field: 'residence', other: 'residence_other', label: 'Country(ies) of legal residence' },
  { field: 'birth_country', other: 'birth_country_other', label: 'Country(ies) of birth' },
];

/**
 * Contact information — rendered once for the client and again for their
 * spouse or partner. `person` only affects copy and field ids.
 */
export default function StepContact({ number, person = 'primary', data, onChange }) {
  useSectionDefaults(data, onChange, SECTION_DEFAULTS.contact);

  const d = data || {};
  const isSpouse = person === 'spouse';
  const p = isSpouse ? 'spouse_contact' : 'contact';
  const who = isSpouse ? "Your Spouse/Partner's" : 'Your';
  const update = (field) => (value) => onChange({ ...d, [field]: value });

  return (
    <div>
      <SectionHeader
        number={number}
        title={`${who} Contact Information`}
        tip={
          isSpouse
            ? 'We need the same details for your spouse or partner so we can open accounts in both names.'
            : 'Please enter your name exactly as it should appear on your financial accounts.'
        }
      />

      {/*
        Both labels are kept to one line on purpose. The explanatory text
        lives in `help`, which renders *below* the input — a long label wraps
        and pushes its own input down, leaving the two boxes in this row
        sitting at different heights.
      */}
      <div className="row g-3">
        <TextInput
          id={`${p}-full-name`}
          label="Full name"
          help="As it should appear on financial accounts, including a middle initial or name if desired. This is based on preference for titling investment accounts."
          col="col-md-6"
          required
          value={d.full_name}
          onChange={update('full_name')}
          autoComplete="off"
        />
        <TextInput
          id={`${p}-preferred-name`}
          label="Preferred name"
          help="What should we call you?"
          col="col-md-6"
          required
          value={d.preferred_name}
          onChange={update('preferred_name')}
          autoComplete="off"
        />
      </div>

      <div className="mt-3">
        <YesNoRadio
          id={`${p}-known-by-other`}
          label="Are you known by another name (maiden name, prefix, suffix)?"
          value={d.known_by_other_name}
          onChange={update('known_by_other_name')}
        />
        {d.known_by_other_name === 'yes' && (
          <div className="row g-3">
            <TextInput
              id={`${p}-other-name`}
              label="Please specify"
              col="col-md-6"
              required
              value={d.other_name}
              onChange={update('other_name')}
              autoComplete="off"
            />
          </div>
        )}
      </div>

      <hr className="my-4" />

      <div className="cashflow-subsection-title mb-2">Security Questions</div>
      {isSpouse ? (
        // Security answers identify the person who calls us, so they are
        // really theirs to choose. Optional here, with a note, rather than
        // forcing the first person to invent answers on their behalf.
        <p className="text-muted small">
          These verify identity on a call to HFF or Schwab. Your spouse or
          partner can set their own with us directly, so leave these blank if
          you would rather they choose them.
        </p>
      ) : (
        <p className="text-muted small">
          These are used to verify your identity when you call HFF or Schwab.
        </p>
      )}
      <div className="row g-3">
        <TextInput
          id={`${p}-mothers-maiden`}
          label="What is your mother's maiden name?"
          col="col-md-6"
          required={!isSpouse}
          value={d.mothers_maiden_name}
          onChange={update('mothers_maiden_name')}
          autoComplete="off"
        />
      </div>
      <div className="row g-3 mt-0">
        <SelectInput
          id={`${p}-security-question`}
          label="Choose a second security question"
          col="col-md-6"
          required={!isSpouse}
          options={SECURITY_QUESTIONS}
          value={d.security_question}
          onChange={update('security_question')}
        />
        <TextInput
          id={`${p}-security-answer`}
          label="Your answer"
          col="col-md-6"
          required={!isSpouse}
          value={d.security_answer}
          onChange={update('security_answer')}
          autoComplete="off"
        />
      </div>

      <hr className="my-4" />

      <div className="row g-3">
        <TextInput
          id={`${p}-phone`}
          label="Primary phone number"
          type="tel"
          col="col-md-4"
          required
          formatter={formatPhone}
          value={d.phone}
          onChange={update('phone')}
          autoComplete="off"
        />
        <TextInput
          id={`${p}-email`}
          label="Email address"
          type="email"
          col="col-md-4"
          required
          value={d.email}
          onChange={update('email')}
          autoComplete="off"
        />
        <TextInput
          id={`${p}-dob`}
          label="Date of birth"
          type="date"
          col="col-md-4"
          required
          min={MIN_DOB}
          max={todayIso()}
          value={d.date_of_birth}
          onChange={update('date_of_birth')}
        />
      </div>

      <div className="mt-4">
        <AddressFields
          idPrefix={`${p}-addr`}
          legend="Legal / home address"
          required
          disallowPoBox
          value={d.address}
          onChange={update('address')}
        />
      </div>

      <hr className="my-4" />

      <div className="row g-3">
        {COUNTRY_FIELDS.map(({ field, other, label }) => (
          <div className="col-md-4" key={field}>
            <ChoiceRadio
              id={`${p}-${field}`}
              label={<>{label} <span className="text-danger">*</span></>}
              options={COUNTRY_OPTIONS}
              value={d[field]}
              onChange={update(field)}
            />
            {d[field] === 'other' && (
              <input
                id={`${p}-${other}`}
                type="text"
                className="form-control"
                placeholder="Please specify"
                value={d[other] ?? ''}
                onChange={(e) => update(other)(e.target.value)}
                autoComplete="off"
              />
            )}
          </div>
        ))}
      </div>

      <div className="mt-4">
        <NotesInput
          value={d.notes}
          onChange={update('notes')}
          label="Anything else we should know about this section?"
          placeholder="Optional"
        />
      </div>
    </div>
  );
}
